import React, { useState, useEffect, useCallback } from 'react';
import { User } from '../types';
import {
  getStandaloneApps,
  getStandaloneSpec,
  getStandaloneOffline,
  packageStandalone,
  deployStandalone,
  listStandaloneProfiles,
  saveStandaloneProfile,
  deleteStandaloneProfile,
  listStandaloneRequests,
  approveStandaloneRequest,
  rejectStandaloneRequest,
  listStandaloneKeys,
  revokeStandaloneKey,
  listAllDeployments,
  deleteDeployment,
  resetDeploymentPassword,
  StandaloneApp,
  StandaloneSpec,
  StandaloneAiConfig,
  StandalonePackageResult,
  StandaloneDeployResult,
  StandaloneProfile,
  StandaloneRequest,
  StandaloneKey,
  Deployment,
  ResetPasswordResult,
} from '../services/api';
import '../styles/StandaloneDeployPage.css';

interface Props {
  user: User;
  onBack: () => void;
}

type Tab = 'package' | 'ssh';
/** Top-level views inside the page. */
type View = 'hub' | 'app' | 'access' | 'deployments';

// Portable Windows Edition — a generic, self-contained build (bundles its own
// Node runtime + SQLite, no Docker, no admin, no install). Because it needs no
// per-install signing/deploy-key, it is served as a plain static download from
// this app's own origin. Override the location with REACT_APP_PORTABLE_WIN_URL.
const PORTABLE_WIN_URL =
  process.env.REACT_APP_PORTABLE_WIN_URL || '/downloads/yakugo-portable-windows.zip';

const AI_FIELDS: { key: keyof StandaloneAiConfig; label: string }[] = [
  { key: 'whisper_url', label: 'Whisper URL (speech-to-text)' },
  { key: 'whisper_model', label: 'Whisper model' },
  { key: 'ollama_url', label: 'Ollama URL (LLM)' },
  { key: 'ollama_model', label: 'Ollama model' },
  { key: 'tts_url', label: 'TTS URL (read aloud)' },
  { key: 'lfm_audio_url', label: 'LFM audio URL' },
];

const EMPTY_AI: StandaloneAiConfig = {
  whisper_url: '',
  whisper_model: '',
  ollama_url: '',
  ollama_model: '',
  tts_url: '',
  lfm_audio_url: '',
};

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleString();
}

function stepIcon(status: string): React.ReactNode {
  const s = (status || '').toLowerCase();
  if (['done', 'success', 'ok', 'completed'].includes(s)) {
    return <span className="step-icon step-ok">✓</span>;
  }
  if (['failed', 'error'].includes(s)) {
    return <span className="step-icon step-fail">✗</span>;
  }
  if (['running', 'in_progress'].includes(s)) {
    return <span className="step-icon step-spinner" />;
  }
  return <span className="step-icon step-skip">–</span>;
}

const StandaloneDeployPage: React.FC<Props> = ({ user, onBack }) => {
  const [view, setView] = useState<View>('hub');

  /* ── Hub list ─────────────────────────────────────────────────── */
  const [appsLoading, setAppsLoading] = useState(true);
  const [appsError, setAppsError] = useState<string | null>(null);
  const [apps, setApps] = useState<StandaloneApp[]>([]);
  const [selectedApp, setSelectedApp] = useState<StandaloneApp | null>(null);

  /* ── App detail: tabs + spec form ─────────────────────────────── */
  const [tab, setTab] = useState<Tab>('package');
  const [specLoading, setSpecLoading] = useState(false);
  const [specError, setSpecError] = useState<string | null>(null);
  const [offlineBusy, setOfflineBusy] = useState(false);
  const [offlineError, setOfflineError] = useState<string | null>(null);
  const [baseSpec, setBaseSpec] = useState<StandaloneSpec | null>(null);
  const [hasAI, setHasAI] = useState(false);

  const [adminEmail, setAdminEmail] = useState('');
  const [domain, setDomain] = useState('');
  const [updateMode, setUpdateMode] = useState<StandaloneSpec['updateMode']>('notify');
  const [httpsPort, setHttpsPort] = useState('443');
  const [httpPort, setHttpPort] = useState('80');
  const [allowedIps, setAllowedIps] = useState('');
  // Container engine baked into the package/deploy — Docker or Podman. Both use
  // the SAME encrypted images; the installer honors this via YAKUGO_ENGINE.
  const [engine, setEngine] = useState<'docker' | 'podman'>('docker');
  const [label, setLabel] = useState('');
  const [ai, setAi] = useState<StandaloneAiConfig>(EMPTY_AI);
  const [aiOpen, setAiOpen] = useState(false);
  // Push-deploy only: also install faster-whisper on the target server itself.
  const [installWhisper, setInstallWhisper] = useState(false);

  /* ── Package tab ──────────────────────────────────────────────── */
  const [packaging, setPackaging] = useState(false);
  const [packageError, setPackageError] = useState<string | null>(null);
  const [packageResult, setPackageResult] = useState<StandalonePackageResult | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  /* ── SSH deploy tab ───────────────────────────────────────────── */
  const [profiles, setProfiles] = useState<StandaloneProfile[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('22');
  const [username, setUsername] = useState('ubuntu');
  const [privateKey, setPrivateKey] = useState('');
  const [deploying, setDeploying] = useState(false);
  const [deployError, setDeployError] = useState<string | null>(null);
  const [deployResult, setDeployResult] = useState<StandaloneDeployResult | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [wipeData, setWipeData] = useState(false);
  // Deploy mode: 'linux' = Ubuntu VM via PEM key, pulling prebuilt ECR images
  // (default). 'synology' = Synology NAS / generic host via PASSWORD auth,
  // building images on the target with sudo (no ECR). Ports default off 443/80
  // since DSM owns those.
  const [deployMode, setDeployMode] = useState<'linux' | 'synology'>('linux');
  const [password, setPassword] = useState('');
  // Auth method is INDEPENDENT of deploy mode. A Linux VM can be reached by an
  // SSH private key OR by username+password (the backend authenticates either
  // way and, for a password login, runs the remote install as root via
  // `sudo -S` on stdin). Synology boxes default to password. Default: key.
  const [authMethod, setAuthMethod] = useState<'key' | 'password'>('key');

  /* ── Access requests & keys (cross-app) ───────────────────────── */
  const [requests, setRequests] = useState<StandaloneRequest[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [keys, setKeys] = useState<StandaloneKey[]>([]);
  const [showRevoked, setShowRevoked] = useState(false);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [accessNotice, setAccessNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  /* ── Deployments view (cross-app) ─────────────────────────────── */
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [deploymentsLoading, setDeploymentsLoading] = useState(false);
  const [deploymentsError, setDeploymentsError] = useState<string | null>(null);
  const [deploymentsNotice, setDeploymentsNotice] = useState<string | null>(null);
  const [resetCreds, setResetCreds] = useState<
    (ResetPasswordResult & { target: string }) | null
  >(null);

  /* ── Load the hub app list ────────────────────────────────────── */
  const loadApps = useCallback(async () => {
    setAppsLoading(true);
    setAppsError(null);
    try {
      const res = await getStandaloneApps();
      setApps(res.apps || []);
    } catch (err) {
      setAppsError(err instanceof Error ? err.message : 'Failed to load apps');
    } finally {
      setAppsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user.isAdmin) loadApps();
  }, [user.isAdmin, loadApps]);

  /* ── Apply a spec to the shared form fields ───────────────────── */
  const applySpec = useCallback((spec: StandaloneSpec, withAI: boolean) => {
    setBaseSpec(spec);
    setAdminEmail(spec.adminEmail || '');
    setDomain(spec.domain || '');
    setUpdateMode(spec.updateMode || 'notify');
    setHttpsPort(String(spec.httpsPort ?? 443));
    setHttpPort(String(spec.httpPort ?? 80));
    setAllowedIps((spec.allowedIps || []).join(', '));
    setEngine(spec.engine === 'podman' ? 'podman' : 'docker');
    if (withAI) setAi({ ...EMPTY_AI, ...(spec.ai || {}) });
  }, []);

  /* ── Load profiles for the selected app ───────────────────────── */
  const loadProfiles = useCallback(async (appId: string) => {
    setProfilesLoading(true);
    try {
      const res = await listStandaloneProfiles(appId);
      setProfiles(res.profiles || []);
    } catch {
      setProfiles([]);
    } finally {
      setProfilesLoading(false);
    }
  }, []);

  /* ── Open an app detail view ──────────────────────────────────── */
  const openApp = useCallback(
    (app: StandaloneApp) => {
      setSelectedApp(app);
      setView('app');
      setTab('package');
      // reset per-app form state
      setHasAI(!!app.hasAI);
      setBaseSpec(null);
      setAi(EMPTY_AI);
      setAiOpen(false);
      setLabel('');
      setPackageResult(null);
      setPackageError(null);
      setDeployResult(null);
      setDeployError(null);
      setSelectedProfileId('');
      setHost('');
      setPort('22');
      setUsername('ubuntu');
      setPrivateKey('');
      setPassword('');
      setDeployMode('linux');
      setAuthMethod('key');
      setWipeData(false);
      setProfiles([]);
      setSpecError(null);

      setSpecLoading(true);
      getStandaloneSpec(app.appId)
        .then(spec => applySpec(spec, !!app.hasAI))
        .catch(err =>
          setSpecError(err instanceof Error ? err.message : 'Failed to load the default spec')
        )
        .finally(() => setSpecLoading(false));

      loadProfiles(app.appId);
    },
    [applySpec, loadProfiles]
  );

  const backToHub = useCallback(() => {
    setView('hub');
    setSelectedApp(null);
  }, []);

  /* ── Access view loader ───────────────────────────────────────── */
  const loadAccess = useCallback(async () => {
    setAccessLoading(true);
    setAccessError(null);
    try {
      const [reqRes, keyRes] = await Promise.all([
        listStandaloneRequests(showAll),
        listStandaloneKeys(),
      ]);
      setRequests(reqRes.requests || []);
      setKeys(keyRes.keys || []);
    } catch (err) {
      setAccessError(err instanceof Error ? err.message : 'Failed to load requests and keys');
    } finally {
      setAccessLoading(false);
    }
  }, [showAll]);

  useEffect(() => {
    if (user.isAdmin && view === 'access') {
      loadAccess();
    }
  }, [user.isAdmin, view, loadAccess]);

  /* ── Deployments view loader ──────────────────────────────────── */
  const loadDeployments = useCallback(async () => {
    setDeploymentsLoading(true);
    setDeploymentsError(null);
    try {
      const res = await listAllDeployments();
      setDeployments(res.deployments || []);
    } catch (err) {
      setDeploymentsError(err instanceof Error ? err.message : 'Failed to load deployments');
    } finally {
      setDeploymentsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user.isAdmin && view === 'deployments') {
      loadDeployments();
    }
  }, [user.isAdmin, view, loadDeployments]);

  /* ── Build current spec from the form ─────────────────────────── */
  const buildSpec = (): StandaloneSpec => {
    const spec: StandaloneSpec = {
      ...(baseSpec || ({} as StandaloneSpec)),
      adminEmail: adminEmail.trim(),
      updateMode,
      engine,
      httpsPort: parseInt(httpsPort, 10) || 443,
      httpPort: parseInt(httpPort, 10) || 80,
      allowedIps: allowedIps
        .split(',')
        .map(s => s.trim())
        .filter(Boolean),
    };
    const dom = domain.trim();
    if (dom) spec.domain = dom; else delete spec.domain;
    if (hasAI) {
      spec.ai = { ...ai };
    } else {
      delete spec.ai;
    }
    return spec;
  };

  const validateSpecForm = (): string | null => {
    if (!adminEmail.trim() || !/\S+@\S+\.\S+/.test(adminEmail.trim())) {
      return 'A valid admin email is required.';
    }
    return null;
  };

  const copyText = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(c => (c === key ? null : c)), 2000);
    } catch {
      /* clipboard unavailable — user can select the text manually */
    }
  };

  /* ── Profile selection ────────────────────────────────────────── */
  const handleSelectProfile = (id: string) => {
    setSelectedProfileId(id);
    setDeployResult(null);
    setDeployError(null);
    setPrivateKey('');
    setPassword('');
    const profile = profiles.find(p => p.id === id);
    if (profile) {
      setHost(profile.host || '');
      setPort(String(profile.port ?? 22));
      setUsername(profile.username || 'ubuntu');
      // Match the saved credential type: password-only profile → password auth.
      if (profile.hasPassword && !profile.hasKey) setAuthMethod('password');
      else if (profile.hasKey) setAuthMethod('key');
      if (profile.spec) applySpec(profile.spec, hasAI);
    }
  };

  const selectedProfile = profiles.find(p => p.id === selectedProfileId) || null;
  // The prebuilt portable Windows bundle is Yakugo-specific; only offer it there.
  const isYakugo = !!selectedApp && /yakugo/i.test(`${selectedApp.appId} ${selectedApp.name}`);

  /* ── Actions ──────────────────────────────────────────────────── */

  // Fetch a presigned link to the prebuilt OFFLINE / air-gapped package and
  // start the download (images baked in — installs with no internet).
  const handleOfflineDownload = useCallback(async () => {
    if (!selectedApp) return;
    setOfflineBusy(true);
    setOfflineError(null);
    try {
      const info = await getStandaloneOffline(selectedApp.appId);
      window.location.href = info.downloadUrl;
    } catch (e: any) {
      setOfflineError(
        e?.message?.includes('404') || /No offline package/i.test(e?.message || '')
          ? 'No offline package is published for this app yet.'
          : e?.message || 'Could not fetch the offline package link.'
      );
    } finally {
      setOfflineBusy(false);
    }
  }, [selectedApp]);
  const handlePackage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedApp) return;
    const problem = validateSpecForm();
    if (problem) {
      setPackageError(problem);
      return;
    }
    setPackaging(true);
    setPackageError(null);
    setPackageResult(null);
    try {
      const result = await packageStandalone(
        selectedApp.appId,
        buildSpec(),
        label.trim() || undefined,
        user.email || undefined
      );
      setPackageResult(result);
    } catch (err) {
      setPackageError(err instanceof Error ? err.message : 'Packaging failed');
    } finally {
      setPackaging(false);
    }
  };

  const handleDeploy = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedApp) return;
    const problem = validateSpecForm();
    if (problem) {
      setDeployError(problem + ' (set it in the spec fields below)');
      return;
    }
    if (!host.trim()) {
      setDeployError('Target IP / host is required.');
      return;
    }
    // Password auth needs a password; key auth needs a PEM key — unless a saved
    // profile already carries that credential.
    if (authMethod === 'password') {
      if (!password.trim() && !(selectedProfile && selectedProfile.hasPassword)) {
        setDeployError('A password is required for username & password auth.');
        return;
      }
    } else if (!privateKey.trim() && !(selectedProfile && selectedProfile.hasKey)) {
      setDeployError('A private key (PEM) is required.');
      return;
    }
    setDeploying(true);
    setDeployError(null);
    setDeployResult(null);
    try {
      const result = await deployStandalone(selectedApp.appId, {
        profileId: selectedProfileId || undefined,
        host: host.trim(),
        port: parseInt(port, 10) || 22,
        username: username.trim() || (authMethod === 'password' ? 'admin' : 'ubuntu'),
        privateKey: authMethod === 'key' ? privateKey.trim() || undefined : undefined,
        password: authMethod === 'password' ? password.trim() || undefined : undefined,
        authMode: authMethod,
        buildOnHost: deployMode === 'synology' || undefined,
        spec: buildSpec(),
        label: label.trim() || undefined,
        wipeData: wipeData || undefined,
        installWhisper: installWhisper || undefined,
      });
      setDeployResult(result);
    } catch (err) {
      setDeployError(err instanceof Error ? err.message : 'Deploy failed');
    } finally {
      setDeploying(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!selectedApp) return;
    const name = window.prompt(
      'Profile name:',
      selectedProfile ? selectedProfile.name : host.trim()
    );
    if (!name || !name.trim()) return;
    if (!host.trim()) {
      setDeployError('Target IP / host is required to save a profile.');
      return;
    }
    setSavingProfile(true);
    setDeployError(null);
    try {
      const res = await saveStandaloneProfile(selectedApp.appId, {
        id: selectedProfileId || undefined,
        name: name.trim(),
        host: host.trim(),
        port: parseInt(port, 10) || 22,
        username: username.trim() || (authMethod === 'password' ? 'admin' : 'ubuntu'),
        privateKey: authMethod === 'key' ? privateKey.trim() || undefined : undefined,
        password: authMethod === 'password' ? password.trim() || undefined : undefined,
        authMode: authMethod,
        buildOnHost: deployMode === 'synology' || undefined,
        spec: buildSpec(),
      });
      await loadProfiles(selectedApp.appId);
      if (res.profile?.id) setSelectedProfileId(res.profile.id);
      setPrivateKey('');
      setPassword('');
    } catch (err) {
      setDeployError(err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (!selectedApp || !selectedProfile) return;
    if (!window.confirm(`Delete saved profile "${selectedProfile.name}"? This cannot be undone.`)) {
      return;
    }
    try {
      await deleteStandaloneProfile(selectedApp.appId, selectedProfile.id);
      setSelectedProfileId('');
      setPrivateKey('');
      await loadProfiles(selectedApp.appId);
    } catch (err) {
      setDeployError(err instanceof Error ? err.message : 'Failed to delete profile');
    }
  };

  const handleApprove = async (req: StandaloneRequest) => {
    if (
      !window.confirm(
        `Approve standalone access for ${req.email}? A download link will be generated and emailed automatically.`
      )
    ) {
      return;
    }
    setBusyId(String(req.id));
    setAccessNotice(null);
    setAccessError(null);
    try {
      const res = await approveStandaloneRequest(req.id, {
        appId: 'yakugo',
        spec: baseSpec ? buildSpec() : undefined,
      });
      setAccessNotice(
        `Approved — emailed download link to ${res.requesterEmail || req.email}` +
          (res.version ? ` (version ${res.version}, key ${res.keyId})` : '')
      );
      await loadAccess();
    } catch (err) {
      setAccessError(err instanceof Error ? err.message : 'Approval failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (req: StandaloneRequest) => {
    const reason = window.prompt(`Reject request from ${req.email}? Optional reason:`, '');
    if (reason === null) return;
    setBusyId(String(req.id));
    setAccessNotice(null);
    setAccessError(null);
    try {
      await rejectStandaloneRequest(req.id, reason.trim() || undefined);
      setAccessNotice(`Rejected request from ${req.email}`);
      await loadAccess();
    } catch (err) {
      setAccessError(err instanceof Error ? err.message : 'Rejection failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleRevoke = async (key: StandaloneKey) => {
    if (
      !window.confirm(
        `Revoke deploy key "${key.label || key.id}"? Installations using it will stop receiving updates.`
      )
    ) {
      return;
    }
    setBusyId(key.id);
    setAccessNotice(null);
    setAccessError(null);
    try {
      await revokeStandaloneKey(key.id);
      setAccessNotice(`Key "${key.label || key.id}" revoked`);
      await loadAccess();
    } catch (err) {
      setAccessError(err instanceof Error ? err.message : 'Revoke failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleDeleteDeployment = async (dep: Deployment) => {
    if (
      !window.confirm(
        `Remove the deployment for ${dep.app} (${dep.domain || dep.host || dep.target})?\n\n` +
          'This UNINSTALLS the app on the server — it stops and removes all containers and ' +
          'data volumes, deletes the install directory, and removes the DNS record. ' +
          'This cannot be undone.'
      )
    ) {
      return;
    }
    setBusyId(dep.id);
    setDeploymentsNotice(null);
    setDeploymentsError(null);
    try {
      const res = await deleteDeployment(dep.app, dep.id);
      const t = res?.teardown;
      let msg = `Removed ${dep.app}`;
      if (t?.attempted) {
        msg += t.ok
          ? ' — server uninstalled (containers + data volumes removed, DNS cleaned)'
          : ` — record removed, but server teardown failed: ${t.detail || 'unknown'} (you may need to clean the box manually)`;
      } else if (t?.detail) {
        msg += ` — ${t.detail}`;
      }
      setDeploymentsNotice(msg);
      await loadDeployments();
    } catch (err) {
      setDeploymentsError(err instanceof Error ? err.message : 'Remove failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleResetPassword = async (dep: Deployment) => {
    const target = dep.domain || dep.host || dep.target || dep.app;
    if (
      !window.confirm(
        `Reset the admin password for ${target}? ` +
          'The current password stops working immediately. A new one will be shown once.'
      )
    ) {
      return;
    }
    setBusyId(dep.id);
    setDeploymentsNotice(null);
    setDeploymentsError(null);
    setResetCreds(null);
    try {
      const res = await resetDeploymentPassword(dep.app, dep.id);
      setResetCreds({ ...res, target });
    } catch (err) {
      setDeploymentsError(err instanceof Error ? err.message : 'Password reset failed');
    } finally {
      setBusyId(null);
    }
  };

  /* ── Admin gate ───────────────────────────────────────────────── */

  if (!user.isAdmin) {
    return (
      <div className="standalone-page">
        <div className="page-header">
          <button className="back-button" onClick={onBack}>← Back to My Apps</button>
          <h1>🔒 Standalone Deploy</h1>
        </div>
        <div className="admin-required">
          <div className="empty-icon">🔒</div>
          <h2>Admin access required</h2>
          <p>This page is restricted to members of the <code>admin</code> group.</p>
        </div>
      </div>
    );
  }

  /* ── Shared spec form fields (used by package + ssh tabs) ─────── */

  const specFormFields = (
    <>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="sd-admin-email">Admin email *</label>
          <input
            id="sd-admin-email"
            type="text"
            value={adminEmail}
            onChange={e => setAdminEmail(e.target.value)}
            placeholder="admin@example.com"
            required
          />
          <small>First login owner of the standalone installation.</small>
        </div>
        <div className="form-group">
          <label htmlFor="sd-update-mode">Update mode</label>
          <select
            id="sd-update-mode"
            value={updateMode}
            onChange={e => setUpdateMode(e.target.value as StandaloneSpec['updateMode'])}
          >
            <option value="notify">Notify — show update banner, admin applies</option>
            <option value="auto">Auto — apply updates automatically</option>
            <option value="manual">Manual — no update checks</option>
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="sd-domain">Domain (optional)</label>
          <input
            id="sd-domain"
            type="text"
            value={domain}
            onChange={e => setDomain(e.target.value)}
            placeholder="e.g. yakugo-team.sae-g.com"
          />
          <small>
            A subdomain under sae-g.com → trusted HTTPS (cert issued by the
            control plane, auto-renewed). Leave blank to use https://&lt;ip&gt;
            with a self-signed certificate.
          </small>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="sd-engine">Container engine</label>
          <select
            id="sd-engine"
            value={engine}
            onChange={e => setEngine(e.target.value as 'docker' | 'podman')}
          >
            <option value="docker">Docker</option>
            <option value="podman">Podman</option>
          </select>
          <small>
            Engine the installer uses on the target. The application images are
            identical (source-protected) either way; Podman runs rootful so
            privileged ports (443/80) bind like Docker.
          </small>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="sd-https-port">HTTPS port</label>
          <input
            id="sd-https-port"
            type="text"
            inputMode="numeric"
            value={httpsPort}
            onChange={e => setHttpsPort(e.target.value)}
            placeholder="443"
          />
        </div>
        <div className="form-group">
          <label htmlFor="sd-http-port">HTTP port</label>
          <input
            id="sd-http-port"
            type="text"
            inputMode="numeric"
            value={httpPort}
            onChange={e => setHttpPort(e.target.value)}
            placeholder="80"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="sd-allowed-ips">Allowed IPs</label>
          <input
            id="sd-allowed-ips"
            type="text"
            value={allowedIps}
            onChange={e => setAllowedIps(e.target.value)}
            placeholder="203.0.113.4/32, 198.51.100.0/24"
          />
          <small>Comma-separated. Leave empty to allow all.</small>
        </div>
        <div className="form-group">
          <label htmlFor="sd-label">Label (optional)</label>
          <input
            id="sd-label"
            type="text"
            value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder="e.g. Customer X on-prem"
          />
          <small>Shown in the Deploy Keys table to identify this build.</small>
        </div>
      </div>

      {hasAI && (
        <div className="ai-group">
          <button
            type="button"
            className="ai-toggle"
            onClick={() => setAiOpen(o => !o)}
            aria-expanded={aiOpen}
          >
            <span className={`chevron ${aiOpen ? 'open' : ''}`}>▸</span>
            AI Services
          </button>
          {aiOpen && (
            <div className="ai-fields">
              <p className="ai-hint">
                Uses current EA infrastructure by default; admin can change later in the app.
              </p>
              {tab === 'ssh' && (
                <label className="wipe-toggle" htmlFor="sd-install-whisper">
                  <input
                    id="sd-install-whisper"
                    type="checkbox"
                    checked={installWhisper}
                    onChange={e => setInstallWhisper(e.target.checked)}
                  />
                  Install Whisper on the target server (current version)
                </label>
              )}
              {tab === 'ssh' && installWhisper && (
                <p className="ai-hint">
                  The deploy also installs <code>faster-whisper-server</code> (CUDA image on GPU
                  boxes, CPU otherwise) with the model above, and points the app's Whisper URL at
                  the local install — the Whisper URL field is ignored.
                </p>
              )}
              <div className="form-row">
                {AI_FIELDS.map(f => (
                  <div className="form-group" key={f.key}>
                    <label htmlFor={`sd-ai-${f.key}`}>{f.label}</label>
                    <input
                      id={`sd-ai-${f.key}`}
                      type="text"
                      value={ai[f.key]}
                      onChange={e => setAi(prev => ({ ...prev, [f.key]: e.target.value }))}
                      disabled={tab === 'ssh' && installWhisper && f.key === 'whisper_url'}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );

  /* ── HUB VIEW ─────────────────────────────────────────────────── */

  if (view === 'hub') {
    return (
      <div className="standalone-page">
        <div className="page-header">
          <button className="back-button" onClick={onBack}>← Back to My Apps</button>
          <div className="header-content">
            <h1>Standalone Deploy</h1>
            <p className="subtitle">
              Deploy and manage self-hosted, encrypted apps on your own machines.
            </p>
          </div>
          <div className="header-actions">
            <button className="btn-secondary" onClick={() => setView('deployments')}>
              📦 Deployments
            </button>
            <button className="btn-secondary" onClick={() => setView('access')}>
              🔑 Access Requests &amp; Keys
            </button>
          </div>
        </div>

        {appsError && (
          <div className="error-banner">
            Failed to load apps: {appsError}
            <button className="btn-secondary" onClick={loadApps} style={{ marginLeft: '0.75rem' }}>
              Retry
            </button>
          </div>
        )}

        {appsLoading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading standalone apps…</p>
          </div>
        ) : apps.length === 0 ? (
          <div className="admin-required">
            <div className="empty-icon">📦</div>
            <h2>No standalone apps yet</h2>
            <p>Apps published for standalone deployment will appear here.</p>
          </div>
        ) : (
          <div className="sd-hub-grid">
            {apps.map(app => (
              <button
                key={app.appId}
                type="button"
                className={`sd-app-card ${app.enabled ? '' : 'disabled'}`}
                onClick={() => openApp(app)}
                disabled={!app.enabled}
              >
                <div className="sd-app-card-head">
                  <span className="sd-app-icon">{app.icon || '📦'}</span>
                  <span className="version-badge">v{app.version}</span>
                </div>
                <h3>{app.name}</h3>
                <p className="sd-app-desc">{app.description}</p>
                <div className="sd-app-meta">
                  {app.hasAI && <span className="sd-tag">AI</span>}
                  {!app.enabled && <span className="sd-tag muted">disabled</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  /* ── ACCESS VIEW (cross-app) ──────────────────────────────────── */

  if (view === 'access') {
    return (
      <div className="standalone-page">
        <div className="page-header">
          <button className="back-button" onClick={backToHub}>← Back to apps</button>
          <div className="header-content">
            <h1>🔑 Access Requests &amp; Keys</h1>
            <p className="subtitle">Cross-app standalone access requests and issued deploy keys.</p>
          </div>
        </div>

        <div className="sd-card">
          <div className="card-head">
            <h2>Access Requests</h2>
            <div className="card-head-actions">
              <label className="show-all-toggle">
                <input
                  type="checkbox"
                  checked={showAll}
                  onChange={e => setShowAll(e.target.checked)}
                />
                Show all requests
              </label>
              <button className="btn-secondary refresh-btn" onClick={loadAccess} title="Refresh">↻</button>
            </div>
          </div>

          {accessNotice && <div className="notice-banner">{accessNotice}</div>}
          {accessError && <div className="error-banner">{accessError}</div>}

          {accessLoading ? (
            <div className="table-loading">
              <div className="loading-spinner"></div>
            </div>
          ) : requests.length === 0 ? (
            <p className="empty-note">{showAll ? 'No requests yet.' : 'No pending requests.'}</p>
          ) : (
            <div className="table-wrap">
              <table className="sd-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Purpose</th>
                    <th>OS</th>
                    <th>Status</th>
                    <th>Requested</th>
                    <th>Decided</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(req => (
                    <tr key={String(req.id)}>
                      <td>{req.email}</td>
                      <td className="purpose-cell" title={req.purpose}>{req.purpose}</td>
                      <td>{req.target_os}</td>
                      <td>
                        <span className={`status-pill req-${req.status}`}>{req.status}</span>
                      </td>
                      <td>{formatDate(req.created_at)}</td>
                      <td>
                        {req.decided_at
                          ? `${formatDate(req.decided_at)}${req.decided_by ? ` by ${req.decided_by}` : ''}`
                          : '—'}
                      </td>
                      <td className="actions-cell">
                        {req.status === 'pending' && (
                          <>
                            <button
                              className="btn-primary btn-table"
                              onClick={() => handleApprove(req)}
                              disabled={busyId === String(req.id)}
                            >
                              {busyId === String(req.id) ? '…' : 'Approve'}
                            </button>
                            <button
                              className="btn-danger btn-table"
                              onClick={() => handleReject(req)}
                              disabled={busyId === String(req.id)}
                            >
                              Reject
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="sd-card">
          <div className="card-head">
            <h2>Deploy Keys</h2>
            <div className="card-head-actions">
              <label className="show-all-toggle">
                <input
                  type="checkbox"
                  checked={showRevoked}
                  onChange={e => setShowRevoked(e.target.checked)}
                />
                Show revoked
              </label>
            </div>
          </div>
          {(() => {
            const visibleKeys = showRevoked ? keys : keys.filter(k => !k.revoked);
            return accessLoading ? (
            <div className="table-loading">
              <div className="loading-spinner"></div>
            </div>
          ) : visibleKeys.length === 0 ? (
            <p className="empty-note">
              {keys.length === 0
                ? 'No deploy keys yet.'
                : 'No active deploy keys — enable “Show revoked” to see revoked ones.'}
            </p>
          ) : (
            <div className="table-wrap">
              <table className="sd-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>App</th>
                    <th>Label</th>
                    <th>Email</th>
                    <th>Version</th>
                    <th>Created</th>
                    <th>Last used</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleKeys.map(key => (
                    <tr key={key.id} className={key.revoked ? 'row-revoked' : ''}>
                      <td>
                        <span className={`status-pill ${key.revoked ? 'key-revoked' : 'key-active'}`}>
                          {key.revoked ? 'revoked' : 'active'}
                        </span>
                      </td>
                      <td>{key.appId || <span className="muted">—</span>}</td>
                      <td>{key.label || <span className="muted">—</span>}</td>
                      <td>{key.requesterEmail || <span className="muted">—</span>}</td>
                      <td>{key.version || '—'}</td>
                      <td>{formatDate(key.createdAt)}</td>
                      <td>{formatDate(key.lastUsedAt)}</td>
                      <td className="actions-cell">
                        {!key.revoked && (
                          <button
                            className="btn-danger btn-table"
                            onClick={() => handleRevoke(key)}
                            disabled={busyId === key.id}
                          >
                            {busyId === key.id ? '…' : 'Revoke'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
          })()}
        </div>
      </div>
    );
  }

  /* ── DEPLOYMENTS VIEW (cross-app) ─────────────────────────────── */

  if (view === 'deployments') {
    return (
      <div className="standalone-page">
        <div className="page-header">
          <button className="back-button" onClick={backToHub}>← Back to apps</button>
          <div className="header-content">
            <h1>📦 Deployments</h1>
            <p className="subtitle">
              Every standalone installation that has been pushed or issued, across all apps.
            </p>
          </div>
          <div className="header-actions">
            <button className="btn-secondary refresh-btn" onClick={loadDeployments} title="Refresh">↻</button>
          </div>
        </div>

        <div className="sd-card">
          {deploymentsNotice && <div className="notice-banner">{deploymentsNotice}</div>}
          {deploymentsError && <div className="error-banner">{deploymentsError}</div>}

          {resetCreds && (
            <div className="creds-box">
              <div className="creds-title">
                New login for {resetCreds.target} (shown once — not stored)
                <button
                  type="button"
                  className="btn-secondary copy-btn creds-dismiss"
                  onClick={() => setResetCreds(null)}
                >
                  Dismiss
                </button>
              </div>
              <div className="creds-row">
                <span className="creds-label">Username</span>
                <code className="creds-value">{resetCreds.adminUser}</code>
              </div>
              <div className="creds-row">
                <span className="creds-label">Password</span>
                <code className="creds-value">{resetCreds.adminPassword}</code>
                <button
                  type="button"
                  className="btn-secondary copy-btn"
                  onClick={() => copyText('reset-pass', resetCreds.adminPassword)}
                >
                  {copied === 'reset-pass' ? 'Copied ✓' : 'Copy'}
                </button>
              </div>
              {resetCreds.note && <p className="deploy-note">{resetCreds.note}</p>}
            </div>
          )}

          {deploymentsLoading ? (
            <div className="table-loading">
              <div className="loading-spinner"></div>
            </div>
          ) : deployments.length === 0 ? (
            <p className="empty-note">No deployments yet.</p>
          ) : (
            <div className="table-wrap">
              <table className="sd-table">
                <thead>
                  <tr>
                    <th>App</th>
                    <th>Target</th>
                    <th>Version</th>
                    <th>Status</th>
                    <th>Method</th>
                    <th>Login</th>
                    <th>Updated</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {deployments.map(dep => {
                    const targetLabel = dep.domain || dep.host || dep.target || '—';
                    return (
                      <tr key={dep.id}>
                        <td>{dep.app}</td>
                        <td>
                          {dep.url ? (
                            <a href={dep.url} target="_blank" rel="noopener noreferrer">
                              {targetLabel}
                            </a>
                          ) : (
                            targetLabel
                          )}
                        </td>
                        <td>{dep.version || '—'}</td>
                        <td>
                          <span
                            className={`status-pill ${dep.status === 'deployed' ? 'key-active' : 'key-revoked'}`}
                          >
                            {dep.status}
                          </span>
                        </td>
                        <td>{dep.method}</td>
                        <td>{dep.adminEmail || <span className="muted">—</span>}</td>
                        <td>{formatDate(dep.updatedAt)}</td>
                        <td className="actions-cell">
                          <button
                            className="btn-secondary btn-table"
                            onClick={() => handleResetPassword(dep)}
                            disabled={busyId === dep.id}
                          >
                            {busyId === dep.id ? 'Resetting…' : 'Reset password'}
                          </button>
                          <button
                            className="btn-danger btn-table"
                            onClick={() => handleDeleteDeployment(dep)}
                            disabled={busyId === dep.id}
                            title="Uninstalls the app on the server (containers + data) and removes its DNS record"
                          >
                            {busyId === dep.id ? 'Removing…' : 'Remove'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ── APP DETAIL VIEW ──────────────────────────────────────────── */

  return (
    <div className="standalone-page">
      <div className="page-header">
        <button className="back-button" onClick={backToHub}>← Back to apps</button>
        <div className="header-content">
          <h1>
            {selectedApp?.icon || '📦'} {selectedApp?.name || 'Standalone Deploy'}
          </h1>
          <p className="subtitle">
            {selectedApp?.description ||
              'Package or push-deploy a self-contained installation.'}
          </p>
        </div>
      </div>

      {specError && (
        <div className="error-banner">
          Failed to load the default spec: {specError} — the forms below start empty.
        </div>
      )}

      <div className="sd-tabs">
        <button className={tab === 'package' ? 'active' : ''} onClick={() => setTab('package')}>
          📦 Generate Installer Package
        </button>
        <button className={tab === 'ssh' ? 'active' : ''} onClick={() => setTab('ssh')}>
          🚀 Push Deploy via SSH
        </button>
      </div>

      {specLoading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading configuration…</p>
        </div>
      ) : (
        <>
          {/* ── a. Generate Installer Package ─────────────────────── */}
          {tab === 'package' && (
            <>
            {isYakugo && (
              <div className="sd-card portable-card">
                <div className="portable-head">
                  <h2>🪟 Portable Windows Edition</h2>
                  <span className="portable-badge">No install · No Docker · No admin</span>
                </div>
                <p className="card-hint">
                  A self-contained build that runs from a single folder — it bundles its own Node
                  runtime and an embedded SQLite database, so nothing needs to be installed on the PC.
                  Download the ZIP, unzip it on the target Windows machine, and double-click
                  <code> Yakugo-Portable.bat</code>. It starts on <code>http://localhost:3900</code>
                  and prints a first-run admin login.
                </p>

                <a className="btn-primary download-link" href={PORTABLE_WIN_URL} download>
                  ⬇ Download Portable (Windows .zip)
                </a>

                <ul className="portable-notes">
                  <li>
                    <strong>Runs locally, no LAN push needed</strong> — the browser only downloads the
                    ZIP; the app itself runs on the target PC.
                  </li>
                  <li>
                    <strong>Not fully offline</strong> — transcription/translation/TTS still call the
                    AI servers (default <code>http://34.204.20.142</code>). The PC needs outbound
                    network to that host; change it later in <em>Settings → AI Services</em>.
                  </li>
                  <li>
                    <strong>Use it on the same PC</strong> — the microphone works on
                    <code> http://localhost</code> (a secure context). Opening it from another device
                    by IP loads the UI but the browser blocks the mic.
                  </li>
                </ul>
              </div>
            )}
            <div className="sd-card portable-card">
                <div className="portable-head">
                  <h2>🔒 Offline / Air-gapped Edition (Docker / Podman)</h2>
                  <span className="portable-badge">No internet · Images bundled</span>
                </div>
                <p className="card-hint">
                  A fully self-contained build for private networks — <strong>every</strong> container
                  image is baked into the ZIP{isYakugo
                    ? ' (backend, web, updater, MySQL)'
                    : ' (backend, web, updater, MySQL, MinIO object storage, Qdrant vector DB)'}
                  , so the target <strong>never pulls from AWS ECR or Docker Hub</strong>. Expect a
                  large download ({isYakugo ? '~400 MB' : '~1.7 GB'}); the installer loads the images
                  locally and starts the stack with no registry access at all.
                </p>

                <button
                  className="btn-primary download-link"
                  onClick={handleOfflineDownload}
                  disabled={offlineBusy}
                >
                  {offlineBusy ? 'Preparing link…' : '⬇ Download Offline Package (.zip)'}
                </button>
                {offlineError && <div className="error-banner" style={{ marginTop: 12 }}>{offlineError}</div>}

                <ul className="portable-notes">
                  <li>
                    <strong>Install on the target</strong> (no internet needed):
                    <code> unzip {selectedApp?.appId || 'app'}-standalone-offline-*.zip -d {selectedApp?.appId || 'app'} &amp;&amp; cd {selectedApp?.appId || 'app'} &amp;&amp; chmod +x install.sh &amp;&amp; sudo ./install.sh</code>
                  </li>
                  <li>
                    <strong>Login</strong>: the admin email and one-time password shown when the
                    package was built. Edit <code>.env</code> for ports/admin before installing.
                  </li>
                  {!isYakugo && (
                    <li>
                      <strong>AI features still need endpoints</strong> — resume parsing, interview
                      transcription and search call Ollama / Whisper. Point them at an in-network
                      server in <em>Settings → AI Services</em> (or at install time in <code>.env</code>);
                      everything else runs entirely on the box.
                    </li>
                  )}
                  {isYakugo && (
                    <li>
                      <strong>No DNS setup needed</strong>: containers get static IPs (<code>10.207.0.x</code>)
                      with <code>extra_hosts</code>, so <code>mysql</code>/<code>backend</code> resolve with no
                      resolver — <code>aardvark-dns</code> is an optional fallback, not required on an air-gapped box.
                    </li>
                  )}
                </ul>
              </div>
            <div className="sd-card">
              <h2>Generate Installer Package</h2>
              <p className="card-hint">
                Builds a signed, self-contained installer with the configuration below and returns a
                time-limited download link.
              </p>

              <form onSubmit={handlePackage}>
                {specFormFields}

                {packageError && <div className="error-banner">{packageError}</div>}

                <button type="submit" className="btn-primary" disabled={packaging}>
                  {packaging ? 'Building package…' : 'Generate Installer Package'}
                </button>
              </form>

              {packageResult && (
                <div className="result-card">
                  <div className="result-head">
                    <span className="version-badge">Version {packageResult.version}</span>
                    <span className="key-id">
                      Key ID: <code>{packageResult.keyId}</code>
                    </span>
                  </div>

                  {packageResult.adminPassword && (
                    <div className="creds-box">
                      <div className="creds-title">
                        Login (save this — shown once, not stored on the server)
                      </div>
                      <div className="creds-row">
                        <span className="creds-label">Username</span>
                        <code className="creds-value">{packageResult.adminUser}</code>
                      </div>
                      <div className="creds-row">
                        <span className="creds-label">Password</span>
                        <code className="creds-value">{packageResult.adminPassword}</code>
                        <button
                          type="button"
                          className="btn-secondary copy-btn"
                          onClick={() => copyText('pkg-pass', packageResult.adminPassword)}
                        >
                          {copied === 'pkg-pass' ? 'Copied ✓' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  )}

                  <a
                    className="btn-primary download-link"
                    href={packageResult.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    ⬇ Download installer (valid 7 days)
                  </a>
                  <small className="expires-note">
                    {packageResult.bundleName ? `${packageResult.bundleName} · ` : ''}
                    Link expires {formatDate(packageResult.downloadExpires)}
                  </small>

                  <div className="instructions">
                    <div className="instruction-block">
                      <div className="instruction-head">
                        <h4>Install on Linux</h4>
                        <button
                          type="button"
                          className="btn-secondary copy-btn"
                          onClick={() => copyText('linux', packageResult.instructions.linux)}
                        >
                          {copied === 'linux' ? 'Copied ✓' : 'Copy'}
                        </button>
                      </div>
                      <pre>{packageResult.instructions.linux}</pre>
                    </div>
                    <div className="instruction-block">
                      <div className="instruction-head">
                        <h4>Install on Windows</h4>
                        <button
                          type="button"
                          className="btn-secondary copy-btn"
                          onClick={() => copyText('windows', packageResult.instructions.windows)}
                        >
                          {copied === 'windows' ? 'Copied ✓' : 'Copy'}
                        </button>
                      </div>
                      <pre>{packageResult.instructions.windows}</pre>
                    </div>
                  </div>
                </div>
              )}
            </div>
            </>
          )}

          {/* ── b. Push Deploy via SSH ────────────────────────────── */}
          {tab === 'ssh' && (
            <div className="sd-card">
              <h2>Push Deploy via SSH</h2>
              <p className="card-hint">
                Installs {selectedApp?.name || 'the app'} directly on a target server over SSH using
                the spec below. Load a saved profile, change the IP if needed, and deploy.
              </p>

              <form onSubmit={handleDeploy}>
                <div className="form-group">
                  <label htmlFor="sd-profile">Saved profiles</label>
                  <div className="profile-row">
                    <select
                      id="sd-profile"
                      value={selectedProfileId}
                      onChange={e => handleSelectProfile(e.target.value)}
                      disabled={profilesLoading}
                    >
                      <option value="">— New deployment (no profile) —</option>
                      {profiles.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.username}@{p.host}:{p.port}){p.hasKey ? ' 🔑' : ''}
                        </option>
                      ))}
                    </select>
                    {selectedProfile && (
                      <button
                        type="button"
                        className="btn-danger btn-table"
                        onClick={handleDeleteProfile}
                        title="Delete this profile"
                      >
                        🗑
                      </button>
                    )}
                  </div>
                  {selectedProfile?.hasKey && (
                    <small className="key-saved-note">🔑 key saved — leave the key field empty to reuse it</small>
                  )}
                  {selectedProfile?.hasPassword && (
                    <small className="key-saved-note">🔒 password saved — leave the password field empty to reuse it</small>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="sd-host">Target IP / host *</label>
                    <input
                      id="sd-host"
                      type="text"
                      value={host}
                      onChange={e => setHost(e.target.value)}
                      placeholder="203.0.113.10 or server.example.com"
                      required
                    />
                    <small>Overrides the profile&apos;s host — change the IP later and re-deploy.</small>
                  </div>
                  <div className="form-group">
                    <label htmlFor="sd-port">SSH port</label>
                    <input
                      id="sd-port"
                      type="text"
                      inputMode="numeric"
                      value={port}
                      onChange={e => setPort(e.target.value)}
                      placeholder="22"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="sd-mode">Deploy mode</label>
                    <select
                      id="sd-mode"
                      value={deployMode}
                      onChange={e => {
                        const m = e.target.value as 'linux' | 'synology';
                        setDeployMode(m);
                        if (m === 'synology') {
                          setAuthMethod('password'); // DSM has no PEM key by default
                          if (!username || username === 'ubuntu') setUsername('admin');
                          if (httpsPort === '443') setHttpsPort('8443');
                          if (httpPort === '80') setHttpPort('8081');
                        } else {
                          setAuthMethod('key');
                          if (username === 'admin') setUsername('ubuntu');
                        }
                      }}
                    >
                      <option value="linux">Linux VM / host</option>
                      <option value="synology">Synology / NAS (DSM)</option>
                    </select>
                    <small>
                      {deployMode === 'synology'
                        ? 'Ports default to 8443/8081 since DSM owns 80/443. The login account must be in the administrators group (sudo docker).'
                        : 'Standard Linux host — pulls the prebuilt, source-protected images from the registry.'}
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="sd-auth">Authentication</label>
                    <select
                      id="sd-auth"
                      value={authMethod}
                      onChange={e => {
                        const a = e.target.value as 'key' | 'password';
                        setAuthMethod(a);
                        // Keep username sensible for the chosen method.
                        if (a === 'password' && (!username || username === 'ubuntu') && deployMode === 'synology') setUsername('admin');
                        if (a === 'key' && username === 'admin' && deployMode === 'linux') setUsername('ubuntu');
                      }}
                    >
                      <option value="key">SSH private key (PEM)</option>
                      <option value="password">Username &amp; password</option>
                    </select>
                    <small>
                      {authMethod === 'password'
                        ? 'Logs in with the account password (also used for sudo on the target). Works on Linux VMs and Synology alike.'
                        : 'Logs in with a PEM private key — the usual choice for cloud Ubuntu hosts.'}
                    </small>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="sd-engine-deploy">Container engine</label>
                  <select
                    id="sd-engine-deploy"
                    value={engine}
                    onChange={e => setEngine(e.target.value as 'docker' | 'podman')}
                  >
                    <option value="docker">Docker</option>
                    <option value="podman">Podman</option>
                  </select>
                  <small>
                    {engine === 'podman'
                      ? 'Target runs Podman (rootful, via sudo) with a compose provider. Same encrypted images as Docker.'
                      : 'Target runs Docker Engine + the compose plugin (default).'}
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="sd-username">Username</label>
                  <input
                    id="sd-username"
                    type="text"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    placeholder={authMethod === 'password' ? (deployMode === 'synology' ? 'admin' : 'ubuntu') : 'ubuntu'}
                  />
                </div>

                {authMethod === 'key' ? (
                  <div className="form-group">
                    <label htmlFor="sd-private-key">
                      Private key (PEM){selectedProfile?.hasKey ? '' : ' *'}
                    </label>
                    <textarea
                      id="sd-private-key"
                      className="pem-input"
                      value={privateKey}
                      onChange={e => setPrivateKey(e.target.value)}
                      placeholder={
                        selectedProfile?.hasKey
                          ? 'Using saved key — paste only to override'
                          : '-----BEGIN RSA PRIVATE KEY-----\n…\n-----END RSA PRIVATE KEY-----'
                      }
                      spellCheck={false}
                    />
                  </div>
                ) : (
                  <div className="form-group">
                    <label htmlFor="sd-password">
                      Password{selectedProfile?.hasPassword ? '' : ' *'}
                    </label>
                    <input
                      id="sd-password"
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder={
                        selectedProfile?.hasPassword
                          ? 'Using saved password — type to override'
                          : 'Target account password (used for SSH + sudo)'
                      }
                      autoComplete="off"
                      spellCheck={false}
                    />
                    <small>
                      The account must be able to <code>sudo docker</code> on the target
                      (e.g. Synology <em>administrators</em> group).
                    </small>
                  </div>
                )}

                <div className="form-group">
                  <label className="wipe-toggle">
                    <input
                      type="checkbox"
                      checked={wipeData}
                      onChange={e => setWipeData(e.target.checked)}
                    />
                    Wipe data (clean reinstall)
                  </label>
                  <small className="wipe-warning">
                    Destroys the existing database &amp; recordings on the target and resets the
                    admin login.
                  </small>
                </div>

                {specFormFields}

                {deployError && <div className="error-banner">{deployError}</div>}

                <div className="ssh-actions">
                  <button type="submit" className="btn-primary" disabled={deploying}>
                    {deploying ? 'Deploying…' : 'Deploy to Server'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleSaveProfile}
                    disabled={savingProfile || deploying}
                  >
                    {savingProfile ? 'Saving…' : selectedProfile ? 'Update profile' : 'Save as profile'}
                  </button>
                </div>
              </form>

              {deploying && (
                <div className="deploy-running">
                  <span className="step-icon step-spinner" />
                  Deploying… this can take up to 10 minutes. Keep this tab open.
                </div>
              )}

              {deployResult && (
                <div className={`result-card ${deployResult.success ? 'result-success' : 'result-failed'}`}>
                  <h3>{deployResult.success ? '✓ Deployment succeeded' : '✗ Deployment failed'}</h3>
                  {deployResult.error && <div className="error-banner">{deployResult.error}</div>}

                  <ul className="deploy-steps">
                    {(deployResult.steps || []).map((step, i) => (
                      <li key={`${step.name}-${i}`}>
                        {stepIcon(step.status)}
                        <span className="step-name">{step.name}</span>
                        {step.detail && <span className="step-detail">{step.detail}</span>}
                      </li>
                    ))}
                  </ul>

                  {deployResult.success && deployResult.url && (
                    <p className="deploy-url">
                      App is live at{' '}
                      <a href={deployResult.url} target="_blank" rel="noopener noreferrer">
                        {deployResult.url}
                      </a>
                    </p>
                  )}
                  {deployResult.success && (deployResult.version || deployResult.keyId) && (
                    <p className="deploy-meta">
                      <small>
                        Version {deployResult.version || '—'} · Key ID{' '}
                        <code>{deployResult.keyId || '—'}</code>
                      </small>
                    </p>
                  )}

                  {deployResult.success && deployResult.adminPassword ? (
                    <div className="creds-box">
                      <div className="creds-title">
                        Login (save this — shown once, not stored on the server)
                      </div>
                      <div className="creds-row">
                        <span className="creds-label">Username</span>
                        <code className="creds-value">{deployResult.adminUser}</code>
                      </div>
                      <div className="creds-row">
                        <span className="creds-label">Password</span>
                        <code className="creds-value">{deployResult.adminPassword}</code>
                        <button
                          type="button"
                          className="btn-secondary copy-btn"
                          onClick={() => copyText('deploy-pass', deployResult.adminPassword || '')}
                        >
                          {copied === 'deploy-pass' ? 'Copied ✓' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    deployResult.success &&
                    deployResult.note && <p className="deploy-note">{deployResult.note}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StandaloneDeployPage;
